import { Link, useParams } from 'react-router';
import { ProjectColorBar } from '../components/ProjectColorBar';
import { StatusPill } from '../components/StatusPill';
import { PhaseCard } from '../components/PhaseCard';
import { MOCK_PROJECT, type MockUpdate, type MockPhase } from '../lib/mockData';

/**
 * /preview/:slug — v2 portal layout running on hardcoded mock data.
 *
 * Layout:
 *   1. Hero header with project identity + Photos button
 *   2. Headlines — pinned updates surfaced from across the project
 *   3. Project Phases — collapsible cards (no photos inside; photos live
 *      on the dedicated /photos page)
 *
 * Updates appear TWICE by design when pinned: large in Headlines (cover
 * story) and inline inside their phase (in-context). Unpinned updates
 * only appear inside their phase.
 */
export function PreviewPortal() {
  const { slug = MOCK_PROJECT.slug } = useParams<{ slug: string }>();
  const project = MOCK_PROJECT;

  // Build the Headlines list: every pinned update across all phases,
  // newest first, with the phase context attached for each entry.
  const pinnedUpdates: Array<{ update: MockUpdate; phase: MockPhase }> = [];
  for (const phase of project.phases) {
    for (const u of phase.updates) {
      if (u.pinned) pinnedUpdates.push({ update: u, phase });
    }
  }
  pinnedUpdates.sort((a, b) => b.update.date.localeCompare(a.update.date));

  return (
    <div className="min-h-screen pb-12">
      {/* Preview banner */}
      <div
        className="text-center text-xs py-1.5 px-3 num"
        style={{
          background: 'var(--color-warning-soft)',
          color: 'var(--color-warning)',
          borderBottom: '1px solid var(--color-warning)',
        }}
      >
        v2 PREVIEW · MOCK DATA · /preview/{slug}
      </div>

      {/* Header — polished card treatment with subtle depth + brand presence */}
      <header
        className="border-b border-[var(--color-border)] relative overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, var(--color-surface) 0%, var(--color-surface-2) 100%)',
        }}
      >
        <ProjectColorBar colorTag={project.color_tag} height={12} />

        {/* Decorative project-color wash on the right edge — adds visual brand
            presence without competing with content. Subtle gradient that fades
            into the surface so it reads as ambient color, not a hard band. */}
        <div
          aria-hidden="true"
          className="absolute right-0 top-3 bottom-0 w-1/3 pointer-events-none hidden md:block"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${rgbaFor(project.color_tag, 0.07)} 70%, ${rgbaFor(project.color_tag, 0.12)} 100%)`,
          }}
        />

        <div className="max-w-5xl mx-auto px-6 py-8 relative">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-0 flex-1">
              <p className="label-mono mb-2" style={{ color: 'var(--color-ink-3)' }}>
                Project Portal
              </p>
              <h1
                className="font-bold text-[var(--color-ink)] tracking-tight leading-tight"
                style={{ fontSize: 'clamp(28px, 5vw, 40px)' }}
              >
                {project.client_name}
              </h1>
              <p className="text-base text-[var(--color-ink-2)] mt-1.5">
                {project.city_state}
              </p>
              <div className="mt-4 inline-flex items-center gap-2 px-2.5 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)]/70">
                <span className="label-mono">Project Code</span>
                <span className="num text-xs text-[var(--color-ink)]">{project.name}</span>
              </div>
            </div>

            {/* Right-side meta block */}
            <div className="flex flex-col items-end gap-3 shrink-0">
              <StatusPill status={project.overall_status} />
              <div className="text-right">
                <p className="label-mono">Target completion</p>
                <p className="num text-base font-semibold text-[var(--color-ink)] mt-0.5">
                  {formatDate(project.target_completion_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom row of header — last updated on left, Photos button on right */}
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap pt-4 border-t border-[var(--color-border)]/60">
            <p className="text-xs text-[var(--color-ink-3)]">
              <span className="label-mono mr-2">Last updated</span>
              <span className="num text-[var(--color-ink-2)]">
                {formatDateTime(project.last_published_at)}
              </span>
            </p>
            <Link
              to={`/preview/${slug}/photos`}
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-md font-semibold text-white shadow-sm hover:shadow-lg active:shadow-sm transition-all hover:-translate-y-0.5 active:translate-y-0"
              style={{ background: 'var(--color-accent)' }}
            >
              <svg
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                strokeLinecap="round" strokeLinejoin="round"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              View Photos
              <span
                className="text-xs num font-bold px-1.5 py-0.5 rounded"
                style={{ background: 'rgba(255,255,255,0.22)' }}
              >
                {project.totalPhotoCount}
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 flex flex-col gap-10">
        {/* Overall project KPIs — one-glance scan, pulled from phase rollup */}
        <ProjectKpiStrip />

        {/* HEADLINES — pinned updates, larger cover-story treatment */}
        {pinnedUpdates.length > 0 && (
          <section>
            <SectionHeading label="Headlines">
              <span className="text-xs text-[var(--color-ink-3)]">
                Latest updates pinned by your PM
              </span>
            </SectionHeading>
            <ol className="flex flex-col gap-4">
              {pinnedUpdates.map(({ update, phase }) => (
                <li
                  key={update.id}
                  className="border border-[var(--color-border)] rounded-md bg-[var(--color-surface)] px-5 py-4"
                  style={{ borderLeft: '4px solid var(--color-accent)' }}
                >
                  <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                    <span className="num text-xs text-[var(--color-ink-2)]">
                      {formatDate(update.date)}
                    </span>
                    <span className="label-mono">{update.log_type}</span>
                    <span
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold"
                      style={{
                        background: 'var(--color-surface-2)',
                        color: 'var(--color-ink-2)',
                      }}
                    >
                      Phase {String(phase.phase_number).padStart(2, '0')} · {phase.phase_name}
                    </span>
                  </div>
                  <p className="text-base text-[var(--color-ink)] leading-relaxed">
                    {update.body}
                  </p>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* PHASES */}
        <section>
          <SectionHeading label="Project Phases">
            <span className="text-xs text-[var(--color-ink-3)]">
              Click any phase to expand
            </span>
          </SectionHeading>
          <ol className="flex flex-col gap-2">
            {project.phases.map((phase) => (
              <li key={phase.id}>
                <PhaseCard phase={phase} />
              </li>
            ))}
          </ol>
        </section>
      </main>

      <footer className="max-w-5xl mx-auto px-6 mt-12 text-xs text-[var(--color-ink-3)] text-center">
        Rough-In Advisory LLC
      </footer>
    </div>
  );
}

/**
 * ProjectKpiStrip — at-a-glance scan numbers (overall % complete,
 * active phase, days to target, total tasks). Pulled from the phase
 * data; in production these would all derive from the publish payload.
 */
function ProjectKpiStrip() {
  const project = MOCK_PROJECT;
  const phases = project.phases;
  const overallPercent = Math.round(
    phases.reduce((acc, p) => acc + p.percent_complete, 0) / Math.max(phases.length, 1),
  );
  const totalTasks = phases.reduce((acc, p) => acc + p.tasks.length, 0);
  const completedTasks = phases.reduce(
    (acc, p) => acc + p.tasks.filter((t) => t.is_complete).length,
    0,
  );
  const activePhase = phases.find((p) => p.status === 'In Progress');
  const daysToTarget = Math.max(
    0,
    Math.round(
      (new Date(project.target_completion_date).getTime() - Date.now()) /
        (1000 * 60 * 60 * 24),
    ),
  );

  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Kpi
        label="Overall"
        value={`${overallPercent}%`}
        sub="project complete"
      />
      <Kpi
        label="Active phase"
        value={activePhase ? `${String(activePhase.phase_number).padStart(2, '0')}` : '—'}
        sub={activePhase ? activePhase.phase_name : 'No active phase'}
      />
      <Kpi
        label="Tasks"
        value={`${completedTasks} / ${totalTasks}`}
        sub="completed"
      />
      <Kpi
        label="Days to target"
        value={daysToTarget.toString()}
        sub={daysToTarget === 1 ? 'day remaining' : 'days remaining'}
      />
    </section>
  );
}

function Kpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="border border-[var(--color-border)] rounded-md bg-[var(--color-surface)] px-4 py-3">
      <p className="label-mono">{label}</p>
      <p className="num text-2xl font-bold text-[var(--color-ink)] mt-0.5 leading-none">
        {value}
      </p>
      <p className="text-[11px] text-[var(--color-ink-3)] mt-1">{sub}</p>
    </div>
  );
}

function SectionHeading({
  label,
  children,
}: {
  label: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between mb-3 border-b border-[var(--color-border)] pb-2 gap-3">
      <h2 className="text-lg font-bold text-[var(--color-ink)] tracking-tight">{label}</h2>
      {children}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Resolve a project color_tag to its `rgba()` form at the requested alpha.
 * Used for the ambient color wash on the header. Mirrors the hex values
 * in lib/colors.ts so the header tint always matches the project's stripe.
 */
function rgbaFor(tag: string, alpha: number): string {
  const HEX: Record<string, string> = {
    amber: '217, 119, 6',
    sage:  '63, 107, 63',
    rust:  '161, 58, 23',
    teal:  '14, 116, 144',
    slate: '71, 85, 105',
    plum:  '107, 33, 168',
    wheat: '168, 162, 158',
  };
  const rgb = HEX[tag] || HEX.amber;
  return `rgba(${rgb}, ${alpha})`;
}

export default PreviewPortal;
